// 物品分类配置文件
// 支持多种匹配规则：exact（精确匹配）、contains（包含匹配）、regex（正则表达式）
// 与 resources.js 中的 RESOURCE_TYPES 对接

import { RESOURCE_TYPES, RESOURCES } from '../resources.js';

// 基于资源类型的自动分类映射
const RESOURCE_TYPE_MAPPING = {
  [RESOURCE_TYPES.HERB]: '材料',
  [RESOURCE_TYPES.MINERAL]: '材料',
  [RESOURCE_TYPES.BEAST_PART]: '材料',
  [RESOURCE_TYPES.SPIRITUAL]: '材料',
  [RESOURCE_TYPES.PILL]: '丹药',
  [RESOURCE_TYPES.ARTIFACT]: '法宝'
};

// 特殊映射：食物归类到材料
const SPECIAL_CATEGORY_MAPPING = {
  '肉食': '材料',
  '灵兽蛋': '材料'
};

export const itemCategories = {
  // 丹药类 - 修炼和恢复用的丹药
  '丹药': {
    priority: 10, // 优先级，数字越大优先级越高
    rules: [
      { type: 'contains', keywords: ['丹', '灵草'] },
      { type: 'exact', keywords: ['聚气丹', '筑基丹', '金丹', '元婴丹', '化神丹', '渡劫丹', '仙丹'] }
    ]
  },

  // 法宝类 - 装备和法器
  '法宝': {
    priority: 9,
    rules: [
      { type: 'contains', keywords: ['法宝', '宝器', '灵器', '仙器', '飞剑'] },
      { type: 'regex', pattern: /.*宝.*/ }, // 包含"宝"字的物品
      { type: 'exact', keywords: ['七星宝刀', '护心玉'] }
    ]
  },

  // 材料类 - 炼制和修炼材料（包含原来的食物类）
  '材料': {
    priority: 8,
    rules: [
      // 包含匹配 - 通用材料关键词
      { type: 'contains', keywords: ['矿', '石', '木材', '玉石', '铁矿', '雪莲', '灵花', '云露', '血参', '树灵果', '幻境结晶', '九叶灵芝', '狼皮', '狼牙', '虎皮', '虎骨', '蛇胆', '龙鳞', '凤羽', '肉食', '灵兽蛋'] },
      // 正则匹配 - 灵石相关
      { type: 'regex', pattern: /.*灵石.*/ },
      // 精确匹配 - 特殊资源物品
      { type: 'exact', keywords: ['紫金矿', '紫灵花', '火精', '雷晶'] }
    ]
  }
};

// 默认分类 - 当物品不匹配任何规则时使用
export const defaultCategory = '其他';

// 分类显示顺序（按优先级降序排列）
export const categoryDisplayOrder = Object.keys(itemCategories)
  .sort((a, b) => itemCategories[b].priority - itemCategories[a].priority);

// 分类图标映射（可选，用于UI扩展）
export const categoryIcons = {
  '丹药': '💊',
  '法宝': '⚔️',
  '材料': '🔮',
  '食物': '🍖',
  '其他': '📦'
};

// 分类描述（可选，用于UI提示）
export const categoryDescriptions = {
  '丹药': '修炼和恢复用的丹药',
  '法宝': '装备和法器，可以提升属性',
  '材料': '炼制和修炼所需的材料',
  '食物': '消耗品，提供临时效果',
  '其他': '未分类物品'
};

// 辅助函数：根据资源类型获取物品分类
export function getCategoryByResourceType(resourceType) {
  return RESOURCE_TYPE_MAPPING[resourceType] || defaultCategory;
}

// 辅助函数：获取物品的资源信息和分类
export function getItemResourceInfo(itemName) {
  const resourceInfo = RESOURCES[itemName];
  if (!resourceInfo) {
    return {
      exists: false,
      category: defaultCategory,
      type: null,
      info: null
    };
  }

  return {
    exists: true,
    category: getCategoryByResourceType(resourceInfo.type),
    type: resourceInfo.type,
    info: resourceInfo
  };
}

// 辅助函数：获取所有指定分类的资源
export function getResourcesByCategory(categoryName) {
  return Object.entries(RESOURCES)
    .filter(([itemName, info]) => getCategoryByResourceType(info.type) === categoryName)
    .map(([itemName, info]) => ({ itemName, ...info }));
}

// 辅助函数：检查物品是否为特定类型
export function isItemOfType(itemName, resourceType) {
  const resourceInfo = RESOURCES[itemName];
  return resourceInfo && resourceInfo.type === resourceType;
}

// 增强分类系统，支持资源类型的自动分类
export function createResourceBasedCategoryRules() {
  const enhancedCategories = { ...itemCategories };

  // 为材料分类添加基于资源的规则
  if (!enhancedCategories['材料']) {
    enhancedCategories['材料'] = { priority: 8, rules: [] };
  }

  // 收集所有资源类型的物品
  Object.entries(RESOURCES).forEach(([itemName, resourceInfo]) => {
    const category = getCategoryByResourceType(resourceInfo.type);
    if (category !== defaultCategory) {
      // 为对应分类添加精确匹配规则
      if (!enhancedCategories[category]) {
        enhancedCategories[category] = { priority: 5, rules: [] };
      }
      enhancedCategories[category].rules.push({
        type: 'exact',
        keywords: [itemName]
      });
    }
  });

  return enhancedCategories;
}

// 验证配置完整性
export function validateConfig() {
  const errors = [];

  // 检查是否有重复的关键字
  const allKeywords = new Set();
  const duplicates = new Set();

  Object.entries(itemCategories).forEach(([category, config]) => {
    config.rules.forEach(rule => {
      if (rule.keywords) {
        rule.keywords.forEach(keyword => {
          if (allKeywords.has(keyword)) {
            duplicates.add(keyword);
          } else {
            allKeywords.add(keyword);
          }
        });
      }
    });
  });

  if (duplicates.size > 0) {
    errors.push(`发现重复的关键字: ${Array.from(duplicates).join(', ')}`);
  }

  // 检查正则表达式是否有效
  Object.entries(itemCategories).forEach(([category, config]) => {
    config.rules.forEach((rule, index) => {
      if (rule.type === 'regex' && rule.pattern) {
        try {
          new RegExp(rule.pattern);
        } catch (e) {
          errors.push(`分类 ${category} 的规则 ${index} 正则表达式无效: ${rule.pattern}`);
        }
      }
    });
  });

  // 检查资源系统一致性
  const resourceItems = Object.keys(RESOURCES);
  const categorizedItems = new Set();

  Object.values(itemCategories).forEach(config => {
    config.rules.forEach(rule => {
      if (rule.keywords) {
        rule.keywords.forEach(keyword => {
          categorizedItems.add(keyword);
        });
      }
    });
  });

  // 找出未分类的资源物品
  const uncategorizedResources = resourceItems.filter(item => !categorizedItems.has(item));
  if (uncategorizedResources.length > 0) {
    errors.push(`发现未在分类规则中定义的资源物品: ${uncategorizedResources.join(', ')}`);
  }

  return {
    valid: errors.length === 0,
    errors,
    uncategorizedResources
  };
}
